import { CONVERSATION_MESSAGE_ITEM_REACTIONS } from '@/config/api/api-routes';
import type {
  GetMessageReactionsResponseDto,
  ToggleReactionRequestDto,
  ToggleReactionResponseDto
} from '@/features/messages/models/message-reaction';
import { http } from '@/shared/api/http';
import type { ResultApi } from '@/shared/types/result-api';

/**
 * Toggle reaction on a message (add/remove)
 * @param conversationId - The conversation ID
 * @param messageId - The message ID
 * @param request - The reaction toggle request
 * @returns Promise resolving to the toggle response
 */
export async function toggleMessageReaction(
  conversationId: string,
  messageId: string,
  request: ToggleReactionRequestDto
): Promise<ToggleReactionResponseDto> {
  const url = CONVERSATION_MESSAGE_ITEM_REACTIONS.replace(':conversationId', conversationId).replace(
    ':messageId',
    messageId
  );

  const response = await http.put<ResultApi<ToggleReactionResponseDto>>(url, request);

  return response.data.data!;
}

/**
 * Get message reactions with pagination support
 * @param conversationId - The conversation ID
 * @param messageId - The message ID
 * @param bucketId - The bucket ID containing the message (format: YYYY-MM)
 * @param reactionFilter - Optional filter by specific emoji
 * @param pageSize - Number of reactions per page (max: 100, default: 50)
 * @param pagingState - Base64 encoded paging state for next page
 * @returns Promise resolving to the message reactions response
 */
export async function getMessageReactions(
  conversationId: string,
  messageId: string,
  bucketId: string,
  reactionFilter?: string,
  pageSize = 50,
  pagingState?: string
): Promise<GetMessageReactionsResponseDto> {
  const url = CONVERSATION_MESSAGE_ITEM_REACTIONS.replace(':conversationId', conversationId).replace(
    ':messageId',
    messageId
  );

  const params = new URLSearchParams({
    bucketId,
    pageSize: Math.min(pageSize, 100).toString()
  });

  if (reactionFilter) {
    params.append('reactionFilter', reactionFilter);
  }

  if (pagingState) {
    params.append('pagingState', pagingState);
  }

  const response = await http.get<ResultApi<GetMessageReactionsResponseDto>>(`${url}?${params.toString()}`);

  return response.data.data!;
}
